import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { authService, SocialConnection } from '../../../services/AuthenticationService';
import { startSocialLoginRedirect } from '../../../services/WebSocialAuth';
import { isUserCancellation, mapAuthError } from '../../../utils/auth-error';
import { routes } from '../../../route-list';
import { LoginScreen } from '../components/LoginScreen';

export const LoginContainer: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // When the user navigates back from the social login redirect (bfcache restores frozen state),
  // reset the loading spinner that was left spinning at redirect time.
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setLoading(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const onLogin = async (email: string, password: string) => {
    setErrorMessage(null);
    setLoading(true);
    try {
      await authService.loginWithPassword(email, password);
      history.replace(routes.HOME);
    } catch (e: any) {
      if (e?.code === 'email_not_verified') {
        history.push(routes.AUTH_VERIFY_EMAIL, { email, message: 'Please verify your email before logging in.' });
        return;
      }
      setErrorMessage(mapAuthError(e));
    } finally {
      setLoading(false);
    }
  };

  const onSocial = async (connection: SocialConnection) => {
    setErrorMessage(null);
    setLoading(true);
    try {
      await startSocialLoginRedirect(connection);
    } catch (e: unknown) {
      if (isUserCancellation(e)) return;
      setErrorMessage(mapAuthError(e));
      setLoading(false);
    }
  };

  return (
    <LoginScreen
      loading={loading}
      errorMessage={errorMessage}
      onLogin={onLogin}
      onSocial={onSocial}
      onForgotPassword={() => history.push(routes.AUTH_FORGOT_PASSWORD)}
      onSignup={() => history.push(routes.AUTH_SIGNUP)}
    />
  );
};
